import {
  analytics,
  getScreenExportFileDownloadEvent,
} from '~entities/amplitude-analytics';

import { downloadFile } from '~shared/lib/download-file';
import { nonNullableValue } from '~shared/lib/non-nullable-value/non-nullable-value';
import { useApi } from '~shared/providers/use-api';

import isEmpty from '@tinkoff/utils/is/empty';

import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import type { CheckReportStatusResponse } from './check-report-status-response';

import type { ReportApi } from '../api';
import { REPORT_API_KEY } from '../api';
import {
  isReportError,
  isReportGenerated,
  isReportInProgress,
} from '../lib/is-report-generated';

export const useCheckReportStatusQuery = (
  reportId: string | undefined | null,
  options: UseQueryOptions<CheckReportStatusResponse> = {}
) => {
  const reportApi = useApi<ReportApi>(REPORT_API_KEY);

  return useQuery({
    queryKey: ['check-status', reportId],
    queryFn: async () => {
      const response = await reportApi.checkReportStatus(
        nonNullableValue(reportId)
      );

      if (isReportError(response)) {
        throw new Error('Check status error');
      }

      return response;
    },
    retry: (count) => count < 3,
    refetchInterval: (data) => (isReportInProgress(data) ? 5000 : false),
    enabled: !isEmpty(reportId),
    ...options,
  });
};

type UseDownloadReportQueryProps = {
  reportData?: CheckReportStatusResponse;
  screen: string;
};

export const useDownloadReportQuery = (
  { reportData, screen }: UseDownloadReportQueryProps,
  options: UseQueryOptions = {}
) => {
  const reportApi = useApi<ReportApi>(REPORT_API_KEY);

  return useQuery({
    queryKey: ['download-report', reportData?.reportId],
    queryFn: async () => {
      const data = await reportApi.downloadReport(
        nonNullableValue(reportData).reportId
      );

      downloadFile(
        data,
        `${screen}_details_${format(
          new Date(),
          "yyyy-MM-dd'T'HH:mm:ss.SSS"
        )}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      );

      analytics.track(
        getScreenExportFileDownloadEvent(screen, data.byteLength)
      );
      return {};
    },
    enabled: isReportGenerated(reportData),
    ...options,
  });
};
